/**
 * Clipboard TSV — compatibil cu Excel in ambele sensuri.
 *
 * Nu e o rafinare: e conditia de tranzitie. Colegele lucreaza in fisier de ani de zile si vor
 * continua sa copieze coloane intre aplicatie si Excel in primele saptamani. Daca `Ctrl+C` din
 * aplicatie nu se lipeste in foaie si invers, aplicatia pare mai proasta decat fisierul chiar
 * daca e mai buna la tot restul.
 *
 * Excel foloseste TAB intre coloane si CRLF intre randuri. O celula care conține tab, ghilimele
 * sau sfarsit de rand se incadreaza in ghilimele duble, iar ghilimelele interioare se dubleaza —
 * aceleasi reguli ca la CSV.
 */

export type Matrice = readonly (readonly string[])[];

const TREBUIE_CITAT = /[\t\r\n"]/;

/** Matrice -> text TSV, gata de pus in clipboard. */
export function laTsv(matrice: Matrice): string {
  return matrice
    .map((rand) =>
      rand
        .map((celula) => {
          if (!TREBUIE_CITAT.test(celula)) return celula;
          return `"${celula.replace(/"/g, '""')}"`;
        })
        .join('\t'),
    )
    .join('\r\n');
}

/**
 * Text TSV -> matrice. Tolerant la ce produce Excel si la ce produce un editor de text:
 * terminatoare `\r\n`, `\n` sau `\r`, si un eventual rand gol la final.
 */
export function dinTsv(text: string): string[][] {
  if (text === '') return [];
  const randuri: string[][] = [];
  let rand: string[] = [];
  let celula = '';
  let inGhilimele = false;
  let i = 0;

  const inchideCelula = (): void => {
    rand.push(celula);
    celula = '';
  };
  const inchideRand = (): void => {
    inchideCelula();
    randuri.push(rand);
    rand = [];
  };

  while (i < text.length) {
    const c = text[i]!;

    if (inGhilimele) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          celula += '"';
          i += 2;
          continue;
        }
        inGhilimele = false;
        i += 1;
        continue;
      }
      celula += c;
      i += 1;
      continue;
    }

    if (c === '"' && celula === '') {
      inGhilimele = true;
      i += 1;
      continue;
    }
    if (c === '\t') {
      inchideCelula();
      i += 1;
      continue;
    }
    if (c === '\r') {
      inchideRand();
      // `\r\n` se consuma ca un singur terminator
      i += text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (c === '\n') {
      inchideRand();
      i += 1;
      continue;
    }
    celula += c;
    i += 1;
  }

  // ultimul rand, daca textul nu se termina cu terminator
  if (celula !== '' || rand.length > 0) inchideRand();

  // Excel adauga adesea un rand gol la final; il taiem, dar numai daca e chiar gol.
  const ultim = randuri[randuri.length - 1];
  if (randuri.length > 1 && ultim && ultim.length === 1 && ultim[0] === '') {
    randuri.pop();
  }
  return randuri;
}

/**
 * Scrie in clipboard. Foloseste API-ul asincron cand exista; altfel cade pe un `<textarea>`
 * ascuns si `execCommand('copy')`, pentru ca in unele contexte (iframe fara permisiune,
 * conexiune ne-securizata) `navigator.clipboard` lipseste.
 */
export async function scrieInClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // cade pe varianta de rezerva
  }
  try {
    const zona = document.createElement('textarea');
    zona.value = text;
    zona.setAttribute('readonly', '');
    zona.style.position = 'fixed';
    zona.style.opacity = '0';
    document.body.appendChild(zona);
    zona.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(zona);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Ce text ajunge in clipboard pentru o zona din grila.
 * `citeste(rand, col)` intoarce textul brut al celulei — sume in format ro-RO, ca lipirea in
 * Excel sa produca numere, nu text.
 */
export function extrageZona(
  zona: { randStart: number; randStop: number; colStart: number; colStop: number },
  citeste: (rand: number, col: number) => string,
): string[][] {
  const out: string[][] = [];
  for (let r = zona.randStart; r <= zona.randStop; r++) {
    const rand: string[] = [];
    for (let c = zona.colStart; c <= zona.colStop; c++) {
      rand.push(citeste(r, c));
    }
    out.push(rand);
  }
  return out;
}
